// src/components/issues/ChoixEquipement.tsx — SCE Issues
// Changer l'équipement d'un problème — administrateur (Lot 7, édition totale).
//
// Le N° de série n'est JAMAIS saisi à la main : il est choisi dans le
// référentiel SCE_Equipements, comme à la déclaration (même recherche, même
// cache mémoire, aucune requête par frappe). OF et PN suivent l'équipement
// choisi ; la catégorie est recalculée par l'API depuis le suffixe.

import { useEffect, useRef, useState, type JSX } from 'react'
import { Package, Search } from 'lucide-react'
import { searchEquipements } from '@/api/equipements.api'
import type { Equipement } from '@/types/issue.types'

/** Même délai et même seuil que le formulaire de déclaration. */
const DEBOUNCE_MS = 150
const MIN_CARACTERES = 2

const STYLE_ETIQUETTE = {
  fontSize: 'var(--font-size-info)',
  color: 'var(--color-text-secondary)',
  display: 'block',
  marginBottom: 6,
} as const

export function ChoixEquipement({
  numeroSerie,
  of,
  pn,
  onChoisir,
  desactive,
}: {
  numeroSerie: string
  of: string
  pn: string
  onChoisir: (eq: Pick<Equipement, 'numeroSerie' | 'of' | 'pn'>) => void
  desactive?: boolean
}): JSX.Element {
  const [recherche, setRecherche] = useState<string | null>(null)
  const [suggestions, setSuggestions] = useState<Equipement[]>([])
  const [erreur, setErreur] = useState<string | null>(null)
  const derniere = useRef(0)

  useEffect(() => {
    if (recherche === null || recherche.trim().length < MIN_CARACTERES) {
      setSuggestions([])
      return
    }
    const numero = ++derniere.current
    const minuteur = setTimeout(() => {
      searchEquipements(recherche.trim())
        .then((r) => {
          if (numero === derniere.current) {
            setSuggestions(r.slice(0, 8))
            setErreur(null)
          }
        })
        .catch(() => {
          if (numero === derniere.current) setErreur('Référentiel équipements injoignable.')
        })
    }, DEBOUNCE_MS)
    return () => clearTimeout(minuteur)
  }, [recherche])

  return (
    <div>
      <span style={STYLE_ETIQUETTE}>Équipement</span>
      <div
        className="flex items-center gap-3"
        style={{
          minHeight: 48,
          padding: '6px 12px',
          borderRadius: 8,
          border: '1px solid var(--color-border)',
          backgroundColor: 'var(--color-gray-5)',
        }}
      >
        <Package size={20} color="var(--color-gray-70)" style={{ flexShrink: 0 }} />
        <span className="flex flex-col flex-1 min-w-0">
          <span style={{ fontSize: 15, fontWeight: 600 }}>{numeroSerie || '—'}</span>
          <span style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>
            {[of && `OF ${of}`, pn && `Projet ${pn}`].filter(Boolean).join(' · ') || 'OF et projet inconnus'}
          </span>
        </span>
        {recherche === null && (
          <button
            type="button"
            onClick={() => setRecherche('')}
            disabled={desactive}
            style={{
              minHeight: 44,
              padding: '0 12px',
              border: 'none',
              background: 'transparent',
              color: 'var(--color-black)',
              fontSize: 'var(--font-size-info)',
              fontWeight: 600,
              textDecoration: 'underline',
            }}
          >
            Changer
          </button>
        )}
      </div>

      {recherche !== null && (
        <div style={{ marginTop: 8 }}>
          <div
            className="flex items-center gap-2 px-3 rounded-lg"
            style={{ height: 48, border: '1px solid var(--color-black)', backgroundColor: 'var(--color-white)' }}
          >
            <Search size={16} color="var(--color-gray-70)" />
            <input
              type="text"
              value={recherche}
              onChange={(e) => setRecherche(e.target.value)}
              placeholder="N° de série, OF ou PN…"
              aria-label="Rechercher un équipement dans le référentiel"
              autoFocus
              disabled={desactive}
              className="flex-1 outline-none border-none bg-transparent"
              style={{ fontSize: 'var(--font-size-copy2)' }}
            />
            <button
              type="button"
              onClick={() => setRecherche(null)}
              style={{ border: 'none', background: 'transparent', fontSize: 'var(--font-size-info)', minHeight: 44 }}
            >
              Annuler
            </button>
          </div>
          {erreur && (
            <p style={{ ...STYLE_ETIQUETTE, color: 'var(--color-error)', margin: '6px 0 0' }}>{erreur}</p>
          )}
          {suggestions.length > 0 && (
            <ul
              role="listbox"
              aria-label="Équipements du référentiel"
              style={{
                margin: '6px 0 0',
                padding: 0,
                listStyle: 'none',
                border: '1px solid var(--color-border)',
                borderRadius: 8,
                overflow: 'hidden',
              }}
            >
              {suggestions.map((eq) => (
                <li key={eq.numeroSerie}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={false}
                    onClick={() => {
                      onChoisir({ numeroSerie: eq.numeroSerie, of: eq.of, pn: eq.pn })
                      setRecherche(null)
                    }}
                    className="flex flex-col w-full text-left"
                    style={{
                      minHeight: 48,
                      padding: '8px 12px',
                      border: 'none',
                      borderTop: '1px solid var(--color-gray-10)',
                      background: 'var(--color-white)',
                    }}
                  >
                    <span style={{ fontSize: 15, fontWeight: 600 }}>{eq.numeroSerie}</span>
                    <span style={{ fontSize: 13, color: 'var(--color-gray-70)' }}>
                      {[eq.of && `OF ${eq.of}`, eq.pn && `Projet ${eq.pn}`, eq.familleEquipement]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}
